/**
 * Ticket details component that displays expanded ticket information
 */

import React, { useState } from 'react';
import { CalendarToday, Receipt } from '@mui/icons-material';
import { Ticket } from '../../types/ticketTypes';
import { WorkNotesSection } from './WorkNotesSection';
import { AppointmentsSection } from './AppointmentsSection';
import { AppointmentForm } from './AppointmentForm';
import { WorkNotesHandlers } from '../hooks/useWorkNotes';
import { AppointmentsHandlers } from '../hooks/useAppointments';

export interface TicketDetailsProps {
  ticket: Ticket;
  tickets: Ticket[];
  workNotesHandlers: WorkNotesHandlers;
  appointmentsHandlers: AppointmentsHandlers;
  onAddInvoiceItem?: () => void;
}

export const TicketDetails: React.FC<TicketDetailsProps> = ({
  ticket,
  tickets,
  workNotesHandlers,
  appointmentsHandlers,
  onAddInvoiceItem
}) => {
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const { editingAppointmentId, setEditingAppointmentId } = appointmentsHandlers;

  const handleAddAppointment = () => {
    // Clear any existing edit state
    setEditingAppointmentId(null);
    setShowAppointmentForm(true);
  };

  const handleCloseAppointmentForm = () => {
    setShowAppointmentForm(false);
    // Clear edit state when closing
    setEditingAppointmentId(null);
  };

  // Show form when editing an appointment
  React.useEffect(() => {
    if (editingAppointmentId) {
      setShowAppointmentForm(true);
    }
  }, [editingAppointmentId]);

  return (
    <div className="ticket-details-expanded">
      <div className="ticket-details-content">
        <div className="ticket-details-section">
          <div className="section-header">
            <h4>Ticket Details</h4>
            <div className="section-header-actions">
              <button
                type="button"
                className="icon-btn"
                onClick={handleAddAppointment}
                title="Add Appointment"
              >
                <CalendarToday style={{ fontSize: '16px' }} />
              </button>
              <button
                type="button"
                className="icon-btn"
                onClick={onAddInvoiceItem}
                title="Add Item to Invoice"
              >
                <Receipt style={{ fontSize: '16px' }} />
              </button>
            </div>
          </div>
          <div className="ticket-details-content-box">
            <div className="detail-row">
              <span className="detail-label">Description:</span>
              <span className="detail-value">{ticket.description}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Priority:</span>
              <span className="detail-value">
                {ticket.priority.toUpperCase()}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Category:</span>
              <span className="detail-value">{ticket.category}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Created:</span>
              <span className="detail-value">{ticket.createdDate}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Customer Email:</span>
              <span className="detail-value">{ticket.customer.email}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Customer Phone:</span>
              <span className="detail-value">{ticket.customer.phone}</span>
            </div>
          </div>
        </div>
        
        <WorkNotesSection 
          ticket={ticket}
          workNotesHandlers={workNotesHandlers}
        />

        <AppointmentsSection 
          ticket={ticket}
          tickets={tickets}
          appointmentsHandlers={appointmentsHandlers}
        />

        {showAppointmentForm && (
          <AppointmentForm
            ticket={ticket}
            tickets={tickets}
            appointmentsHandlers={appointmentsHandlers}
            isVisible={showAppointmentForm}
            onClose={handleCloseAppointmentForm}
          />
        )}
      </div>
    </div>
  );
};